import type { FC, ReactNode } from 'react'
import type { DoorStyle } from '@/types'

import { useRef } from 'react'
import Image from 'next/image'
import { ArrowDownRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMoney, pad } from '@/lib/format'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'
import { Reveal } from '@/components/motion/reveal'
import { doors, doorStyles, doorSteps } from '@/constants/doors'

const styleSummaries: Record<DoorStyle, string> = {
    Traditional: "Familiar raised and short panel designs, in steel or a wood-look composite.",
    'Carriage house': "Barn-style panels with handles, suited to craftsman and farmhouse homes.",
    Modern: "Smooth flush faces and planks, with nothing to break the line.",
    'Full-view glass': "Clear or frosted glass held in aluminum frames."
}

const styleOverview = doorStyles.map(style => {
    const models = doors.filter(door => door.style === style)

    return {
        style,
        image: models[0]?.image ?? '',
        count: models.length,
        from: Math.min(...models.map(door => door.priceFrom))
    }
})

interface DoorStyleIndexProps {
    activeStyle: DoorStyle | null
    onSelect: (style: DoorStyle) => void
}

export const DoorStyleIndex: FC<DoorStyleIndexProps> = ({ activeStyle, onSelect }): ReactNode => {
    return (
        <Reveal selector=':scope > li' as='ul' className='grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4'>
            {styleOverview.map((item, index) => {
                const active = activeStyle === item.style

                return (
                    <li key={item.style}>
                        <button
                            type='button'
                            onClick={() => onSelect(item.style)}
                            aria-pressed={active}
                            className={cn(
                                'group relative flex h-full w-full flex-col overflow-hidden rounded-[24px] text-left outline-none ring-offset-4 ring-offset-cream transition-shadow duration-500 focus-visible:ring-2 focus-visible:ring-ember sm:rounded-[28px]',
                                active && 'ring-2 ring-ember'
                            )}
                        >
                            <span className='relative block aspect-[4/5] w-full overflow-hidden bg-sand'>
                                <Image
                                    src={item.image}
                                    alt=''
                                    fill
                                    sizes='(min-width: 1024px) 25vw, 50vw'
                                    className='object-cover transition-transform duration-[1400ms] ease-expo group-hover:scale-[1.07]'
                                />

                                <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent' />

                                <span className='absolute left-3 top-3 font-mono text-[11px] text-cream/70 sm:left-5 sm:top-5'>
                                    {pad(index + 1)}
                                </span>

                                <span
                                    className={cn(
                                        'absolute right-3 top-3 flex size-8 items-center justify-center rounded-full transition-all duration-500 ease-expo sm:right-5 sm:top-5 sm:size-10',
                                        active ? 'bg-ember text-white' : 'bg-cream/15 text-cream backdrop-blur-md group-hover:bg-cream group-hover:text-ink'
                                    )}
                                >
                                    {active
                                        ? <Check className='size-4' aria-hidden='true' />
                                        : <ArrowDownRight className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-[-45deg]' aria-hidden='true' />}
                                </span>

                                <span className='absolute inset-x-0 bottom-0 p-4 text-cream sm:p-6'>
                                    <span className='block font-heading text-[1.25rem] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[1.75rem]'>
                                        {item.style}
                                    </span>

                                    <span className='mt-2 hidden text-[14px] leading-snug text-cream/70 sm:block'>
                                        {styleSummaries[item.style]}
                                    </span>

                                    <span className='mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10.5px] uppercase tracking-[0.14em] text-cream/75 sm:mt-4 sm:text-[11px]'>
                                        <span>{item.count} {item.count === 1 ? "model" : "models"}</span>
                                        <span aria-hidden='true' className='size-1 rounded-full bg-ember-soft' />
                                        <span>From {formatMoney(item.from)}</span>
                                    </span>
                                </span>
                            </span>
                        </button>
                    </li>
                )
            })}
        </Reveal>
    )
}

export const DoorProcess: FC = (): ReactNode => {
    const ref = useRef<HTMLOListElement>(null)

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add({
            desktop: `(min-width: 1024px) and ${MOTION_QUERY}`,
            mobile: `(max-width: 1023px) and ${MOTION_QUERY}`
        }, context => {
            const { desktop } = context.conditions as { desktop: boolean }
            const line = element.querySelector<HTMLElement>('[data-progress]')
            const dots = gsap.utils.toArray<HTMLElement>('[data-dot]', element)
            const bodies = gsap.utils.toArray<HTMLElement>('[data-step-body]', element)

            if (line) {
                gsap.fromTo(
                    line,
                    desktop ? { scaleX: 0 } : { scaleY: 0 },
                    {
                        ...(desktop ? { scaleX: 1 } : { scaleY: 1 }),
                        ease: 'none',
                        scrollTrigger: { trigger: element, start: 'top 75%', end: 'bottom 55%', scrub: 0.6 }
                    }
                )
            }

            dots.forEach((dot, index) => {
                gsap.fromTo(
                    dot,
                    { backgroundColor: '#f4f1ea', color: '#5c6570' },
                    {
                        backgroundColor: '#15171a',
                        color: '#f4f1ea',
                        duration: 0.4,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: element,
                            start: () => `top+=${(index / Math.max(dots.length - 1, 1)) * element.offsetHeight * 0.6} 70%`,
                            toggleActions: 'play none none reverse'
                        }
                    }
                )
            })

            gsap.from(bodies, {
                y: 30,
                autoAlpha: 0,
                duration: 1.1,
                ease: 'expo.out',
                stagger: 0.12,
                scrollTrigger: { trigger: element, start: 'top 80%', once: true }
            })
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <ol ref={ref} className='relative grid gap-10 pl-14 lg:grid-cols-4 lg:gap-8 lg:pl-0 lg:pt-16'>
            <span aria-hidden='true' className='absolute bottom-2 left-[19px] top-2 w-px bg-ink/10 lg:inset-x-0 lg:bottom-auto lg:left-0 lg:top-[19px] lg:h-px lg:w-auto' />

            <span
                aria-hidden='true'
                data-progress
                className='absolute bottom-2 left-[19px] top-2 w-px origin-top bg-ember lg:inset-x-0 lg:bottom-auto lg:left-0 lg:top-[19px] lg:h-px lg:w-auto lg:origin-left'
            />

            {doorSteps.map((step, index) => (
                <li key={step.title} className='relative'>
                    <span
                        data-dot
                        className='absolute -left-14 top-0 flex size-10 items-center justify-center rounded-full border border-ink/15 bg-ink font-mono text-[12px] text-cream lg:-top-16 lg:left-0'
                    >
                        {pad(index + 1)}
                    </span>

                    <div data-step-body>
                        <h3 className='font-heading text-[1.6rem] font-semibold leading-[1.05] tracking-[-0.035em] text-ink sm:text-[1.9rem]'>
                            {step.title}
                        </h3>

                        <p className='mt-3 max-w-[30ch] text-[15.5px] leading-relaxed text-slate'>
                            {step.body}
                        </p>
                    </div>
                </li>
            ))}
        </ol>
    )
}

const R_MAX = 18

const benchmarks = [
    { value: 0, label: "No insulation", short: "R-0" },
    { value: 9, label: "Good", short: "R-9" },
    { value: 16, label: "Best with a room above", short: "R-16+" }
]

const rNumber = (value: string): number => Number(value.replace(/\D/g, '')) || 0

const sortedDoors = [...doors].sort((a, b) => rNumber(b.rValue) - rNumber(a.rValue))

export const RValueChart: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.from(gsap.utils.toArray<HTMLElement>('[data-bar]', element), {
                scaleX: 0,
                duration: 1.4,
                ease: 'expo.out',
                stagger: 0.07,
                scrollTrigger: { trigger: element, start: 'top 80%', once: true }
            })
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <div ref={ref} className='rounded-[28px] border border-ink/[0.08] bg-card p-5 sm:p-8'>
            <div className='relative mb-6 h-5 sm:ml-[13rem] md:h-10'>
                {benchmarks.map(mark => (
                    <span
                        key={mark.short}
                        className={cn(
                            'absolute top-0 flex flex-col font-mono text-[10.5px] uppercase tracking-[0.12em] text-slate',
                            mark.value === 0 ? 'items-start' : mark.value >= 16 ? '-translate-x-full items-end text-right' : '-translate-x-1/2 items-center text-center'
                        )}
                        style={{ left: `${(mark.value / R_MAX) * 100}%` }}
                    >
                        <span className='text-ink'>{mark.short}</span>
                        <span className='hidden whitespace-nowrap md:block'>{mark.label}</span>
                    </span>
                ))}
            </div>

            <ul className='space-y-4 sm:space-y-3'>
                {sortedDoors.map(door => {
                    const value = rNumber(door.rValue)
                    const best = value >= 16

                    return (
                        <li key={door.id} className='grid items-center gap-2 sm:grid-cols-[13rem_1fr] sm:gap-0'>
                            <span className='flex items-baseline justify-between gap-3 pr-4 sm:block'>
                                <span className='block text-[15px] font-medium text-ink'>{door.name}</span>
                                <span className='block font-mono text-[11px] uppercase tracking-[0.12em] text-slate'>{door.style}</span>
                            </span>

                            <span className='relative flex h-9 items-center overflow-hidden rounded-full bg-sand/80'>
                                <span
                                    data-bar
                                    className={cn('absolute inset-y-0 left-0 origin-left rounded-full', best ? 'bg-ember' : 'bg-ink')}
                                    style={{ width: `${Math.max((value / R_MAX) * 100, 14)}%` }}
                                />

                                <span className='relative pl-4 font-mono text-[12.5px] font-medium text-cream'>
                                    {door.rValue}
                                </span>
                            </span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
